import React, { useEffect, useRef, useState } from "react";

import { Close } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import TriUploadFile from "@/common/TriUploadFile";

interface ImageSearchModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (imageData: string) => void;
  onClear: () => void;
  initialImageData?: string | null;
}

const ImageSearchModal: React.FC<ImageSearchModalProps> = ({
  open,
  onClose,
  onConfirm,
  onClear,
  initialImageData,
}) => {
  const [imageData, setImageData] = useState<string | null>(
    initialImageData || null,
  );
  const imageInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation("bulletin/searchbar");

  // Update imageData when initialImageData changes or when modal opens
  useEffect(() => {
    if (open) {
      // When modal opens, restore from initialImageData if available
      if (initialImageData) {
        setImageData(initialImageData);
      }
    }
  }, [initialImageData, open]);

  const handleImageUpload = (event: any, files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    if (!file.type.startsWith("image/")) {
      // You might want to show an error toast here
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setImageData(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleConfirm = () => {
    if (imageData) {
      onConfirm(imageData);
    }
  };

  const handleClearImage = () => {
    setImageData(null);
    if (imageInputRef.current) {
      imageInputRef.current.value = "";
    }
    onClear();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t("imageSearch")}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {!imageData ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <TriUploadFile
              fileInput={imageInputRef}
              handleFileUpload={handleImageUpload}
              accept="image/*"
              multiple={false}
              uploadMessage={t("uploadImageForSearch")}
            />
          </div>
        ) : (
          <div style={{ textAlign: "center", marginTop: "20px" }}>
            <img
              src={imageData}
              alt="Search image"
              style={{
                maxWidth: "100%",
                maxHeight: "400px",
                objectFit: "contain",
              }}
            />
          </div>
        )}
      </DialogContent>
      <DialogActions>
        {imageData && (
          <Button
            variant="outlined"
            onClick={handleClearImage}
            sx={{ marginRight: "auto" }}
          >
            {t("clearImage")}
          </Button>
        )}
        <Button onClick={onClose}>{t("cancel")}</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!imageData}
        >
          {t("confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageSearchModal;
